import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../Auth/AuthContext';
import axios from 'axios';
import { API_CONFIG } from '../../config/api';
import ChartComponent from './ChartComponent';

const UsageStats = () => {
  const [usageData, setUsageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('month');
  const [chartType, setChartType] = useState('combined'); // 'combined', 'text', 'audio'

  const { getJwtToken } = useAuth();

  // Controller for API request cancellation
  const [controller, setController] = useState(null);

  // Fetch usage data on component mount and when period changes
  useEffect(() => {
    fetchUsageData();
    
    // Cleanup function to cancel any ongoing requests when unmounting or when period changes
    return () => {
      if (controller) {
        controller.abort();
      }
    };
  }, [period]);

  // Fetch usage statistics
  const fetchUsageData = async () => {
    // Cancel any previous ongoing request
    if (controller) {
      controller.abort();
    }

    // Create a new AbortController for this request
    const newController = new AbortController();
    setController(newController);
    
    try {
      setLoading(true);
      setError(null);
      
      let token;
      try {
        token = await getJwtToken();
      } catch (tokenErr) {
        console.error('Error getting JWT token:', tokenErr);
        setError('Authentication error. Please try signing out and signing in again.');
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${API_CONFIG.DASHBOARD_API_URL}/usage?period=${period}`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        signal: newController.signal
      });
      
      // Only update state if this is the most recent request (not aborted)
      setUsageData(response.data);
    } catch (err) {
      // Don't update state if request was aborted
      if (err.name === 'AbortError' || err.name === 'CanceledError') {
        console.log('Usage stats request was cancelled');
        return;
      }
      console.error('Error fetching usage data:', err);
      
      // Detailed error logging
      if (err.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('Error response data:', err.response.data);
        console.error('Error response status:', err.response.status);
        console.error('Error response headers:', err.response.headers);
        setError(`Failed to fetch usage data: ${err.response.status} ${JSON.stringify(err.response.data)}`);
      } else if (err.request) {
        // The request was made but no response was received
        console.error('Error request:', err.request);
        setError('Failed to fetch usage data: No response received from server. CORS issue or network error.');
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('Error message:', err.message);
        setError(`Failed to fetch usage data: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Prepare chart data - memoize to prevent unnecessary recalculations
  const prepareChartData = useCallback(() => {
    if (!usageData || !usageData.daily_usage) {
      return {
        labels: [],
        datasets: []
      };
    }

    // Sort data by date
    const sortedData = [...usageData.daily_usage].sort((a, b) => 
      new Date(a.date) - new Date(b.date)
    );

    // Extract dates for labels
    const labels = sortedData.map(item => {
      const date = new Date(item.date);
      return date.toLocaleDateString();
    });

    // Create datasets based on chart type
    let datasets = [];

    if (chartType === 'combined' || chartType === 'text') {
      datasets.push({
        label: 'Text Requests',
        data: sortedData.map(item => item.text_count || 0),
        borderColor: 'rgb(53, 162, 235)',
        backgroundColor: 'rgba(53, 162, 235, 0.5)',
      });
    }

    if (chartType === 'combined' || chartType === 'audio') {
      datasets.push({
        label: 'Audio Requests',
        data: sortedData.map(item => item.audio_count || 0),
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.5)',
      });
    }

    return { labels, datasets };
  }, [usageData, chartType]);

  // Chart options
  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'API Usage Over Time',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Number of Requests'
        }
      },
      x: {
        title: {
          display: true,
          text: 'Date'
        }
      }
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-800">Usage Statistics</h2>
        <div className="flex space-x-2">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="year">Last 12 Months</option>
          </select>
          <select
            value={chartType}
            onChange={(e) => setChartType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="combined">All Requests</option>
            <option value="text">Text Only</option>
            <option value="audio">Audio Only</option>
          </select>
        </div>
      </div>
      
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
          <p className="font-bold">Error:</p>
          <p>{error}</p>
        </div>
      )}
      
      {loading ? (
        <div className="text-center py-12">Loading usage data...</div>
      ) : !usageData ? (
        <div className="text-center py-12 text-gray-500">
          No usage data available.
        </div>
      ) : (
        <>
          {/* Usage Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-800 mb-1">Total Requests</h3>
              <p className="text-3xl font-bold text-blue-600">
                {usageData.total_requests || 0}
              </p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-green-800 mb-1">Text Requests</h3>
              <p className="text-3xl font-bold text-green-600">
                {usageData.text_requests || 0}
              </p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-purple-800 mb-1">Audio Requests</h3>
              <p className="text-3xl font-bold text-purple-600">
                {usageData.audio_requests || 0}
              </p>
            </div>
          </div>
          
          {/* Usage Chart */}
          <div className="h-80">
            {usageData.daily_usage && usageData.daily_usage.length > 0 ? (
              <ChartComponent options={chartOptions} data={prepareChartData()} />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                No usage data available for the selected period.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default UsageStats;
